import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import { Lock, Eye, EyeOff } from 'lucide-react';
import '../../styles/auth/SetPassword.css';

const SetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      // Appeler le bon endpoint avec token_type pour reset password
      const response = await api.get(`/api/auth/validate-token?token=${token}&token_type=password_reset`);
      if (response.data.valid) {
        setValid(true);
        setEmail(response.data.user?.email || '');
      } else {
        setErrorMessage(response.data.message || 'Token invalide');
      }
    } catch (error) {
      console.error('Token validation error:', error);
      setErrorMessage('Ce lien de réinitialisation est invalide ou a expiré');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      alert('Les mots de passe ne correspondent pas');
      return;
    }

    if (password.length < 8) {
      alert('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (!/[A-Z]/.test(password)) {
      alert('Le mot de passe doit contenir au moins une majuscule');
      return;
    }

    if (!/[0-9]/.test(password)) {
      alert('Le mot de passe doit contenir au moins un chiffre');
      return;
    }

    setLoading(true);

    try {
      await api.post('/api/auth/set-password', {
        token,
        new_password: password
      });
      setSuccess(true);
      setTimeout(() => navigate('/auth/login'), 3000);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="set-password-page">
        <div className="set-password-container">
          <p>Validation de votre invitation...</p>
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="set-password-page">
        <div className="set-password-container">
          <div className="error-section">
            <h2>Invitation invalide</h2>
            <p>{errorMessage}</p>
            <p className="info">Contactez votre administrateur pour obtenir une nouvelle invitation.</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="set-password-page">
        <div className="set-password-container">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Bienvenue chez NVF ! 🌱</h2>
            <p>Votre compte a été activé avec succès.</p>
            <p className="info">Vous pouvez maintenant vous connecter avec votre email et mot de passe.</p>
            <p className="redirect">Redirection vers la connexion...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="set-password-page">
      <div className="set-password-container">
        <div className="logo-section">
          <h1>NVF</h1>
          <p>Naturellement Vert Fertilisation</p>
        </div>

        <h2>Créez votre mot de passe</h2>
        <p className="subtitle">Bienvenue ! Créez un mot de passe sécurisé pour accéder à votre compte.</p>
        {email && <p className="email-display">📧 {email}</p>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">
              <Lock size={18} />
              Mot de passe
            </label>
            <div className="password-input">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 8 caractères"
                required
                autoFocus
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirm">
              <Lock size={18} />
              Confirmer le mot de passe
            </label>
            <div className="password-input">
              <input
                id="confirm"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Retapez le mot de passe"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="password-requirements">
            <p>Le mot de passe doit contenir :</p>
            <ul>
              <li className={password.length >= 8 ? 'valid' : ''}>Au moins 8 caractères</li>
              <li className={/[A-Z]/.test(password) ? 'valid' : ''}>Au moins 1 majuscule</li>
              <li className={/[0-9]/.test(password) ? 'valid' : ''}>Au moins 1 chiffre</li>
            </ul>
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Activation...' : 'Activer mon compte'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SetPassword;